import {assert} from 'Asserts';
import {AssaysFeature, RecipesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize, prependAOrAn} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {FilterKey, SortKey} from 'search/SearchConstants';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {capitalize} from 'util/NameUtil';

import {CreateExperimentTaskRequestMetadataCreate} from './sidepanel/ExperimentCreationTrayTask';

// Only vaid for procedure based studies (assay development studies and process development studies)
const PROCEDURE_TYPE_TO_STUDY_TYPE: Record<ProcedureType, StudyType> = {
  [ProcedureType.ASSAY]: StudyType.ASSAY_DEVELOPMENT,
  [ProcedureType.RECIPE]: StudyType.PROCESS_DEVELOPMENT,
};

// Keep in sync with /src/benchling/studies/domain/constants.py.STUDY_TYPE_TO_PROCEDURE_TYPE
const STUDY_TYPE_TO_PROCEDURE_TYPE: Record<StudyType, ProcedureType | null> = {
  [StudyType.ASSAY_DEVELOPMENT]: ProcedureType.ASSAY,
  [StudyType.PROCESS_DEVELOPMENT]: ProcedureType.RECIPE,
  [StudyType.CLINICAL]: ProcedureType.ASSAY,
  [StudyType.CUSTOM]: null,
  [StudyType.PRECLINICAL]: ProcedureType.ASSAY,
};

const PROCEDURE_TYPE_TO_UNIT_OPERATION_MODEL_TYPE_CONSTANTS: Record<ProcedureType, ModelTypeConstants> = {
  [ProcedureType.ASSAY]: ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK,
  [ProcedureType.RECIPE]: ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK,
};

const PROCEDURE_TYPE_TO_RUN_TASK_FILTER_KEY: Record<ProcedureType, FilterKey> = {
  [ProcedureType.ASSAY]: FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS,
  [ProcedureType.RECIPE]: FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS,
};

const PROCEDURE_TYPE_TO_METHOD_SORT_KEY: Record<ProcedureType, SortKey> = {
  [ProcedureType.ASSAY]: SortKey.SORT_ASSAY_METHOD_ASSAY_ORDER,
  [ProcedureType.RECIPE]: SortKey.SORT_RECIPE_METHOD_RECIPE_ORDER,
};

export function getUnitOperationModelTypeConstantsForProcedureType(
  procedureType: ProcedureType
): ModelTypeConstants {
  return PROCEDURE_TYPE_TO_UNIT_OPERATION_MODEL_TYPE_CONSTANTS[procedureType];
}

export function getRunTaskFilterKeyForProcedureType(procedureType: ProcedureType): FilterKey {
  return PROCEDURE_TYPE_TO_RUN_TASK_FILTER_KEY[procedureType];
}

export function getMethodSortKeyForProcedureType(procedureType: ProcedureType): SortKey {
  return PROCEDURE_TYPE_TO_METHOD_SORT_KEY[procedureType];
}

export function getProcedureTemplateStringForStudyType(
  studyType: StudyType,
  stringCase: StringCase = StringCase.LOWER
): string {
  const procedureType = getProcedureTypeForStudyType(studyType);
  return getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {stringCase});
}

export function getStudyTypeForProcedureType(procedureType: ProcedureType): StudyType {
  return PROCEDURE_TYPE_TO_STUDY_TYPE[procedureType];
}

const PROCEDURE_TYPE_TO_MODEL_TYPE: Record<ProcedureType, Array<ModelTypeConstants>> = {
  [ProcedureType.ASSAY]: [
    ModelTypeConstants.ASSAY_RUN_TASK,
    ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK,
    ModelTypeConstants.ASSAY_STEP_TASK,
  ],
  [ProcedureType.RECIPE]: [
    ModelTypeConstants.RECIPE_RUN_TASK,
    ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK,
    ModelTypeConstants.RECIPE_STEP_TASK,
  ],
};

const MODEL_TYPE_TO_PROCEDURE_TYPE = Object.fromEntries(
  Object.entries(PROCEDURE_TYPE_TO_MODEL_TYPE).flatMap(([procedureType, modelTypes]) =>
    modelTypes.map((modelType) => [modelType, procedureType])
  )
) as Record<ModelTypeConstants, ProcedureType>;

export function getProcedureTypeForModelType(modelType: ModelTypeConstants): ProcedureType | null {
  const procedureType = MODEL_TYPE_TO_PROCEDURE_TYPE[modelType];
  return procedureType;
}

export function getProcedureTypeForStudyTypeAllowNull(studyType: StudyType): ProcedureType | null {
  return STUDY_TYPE_TO_PROCEDURE_TYPE[studyType];
}

export function getProcedureTypeForStudyType(studyType: StudyType): ProcedureType {
  const procedureType = getProcedureTypeForStudyTypeAllowNull(studyType);
  assert(procedureType != null, `${studyType} studies do not have a respective procedure type.`);
  return procedureType;
}

export function getProcedureRunUIStringForStudyType(
  studyType: StudyType,
  {
    plural = false,
    capital = false,
    includeArticle = false,
  }: {plural?: boolean; capital?: boolean; includeArticle?: boolean} = {}
): string {
  const STUDY_TYPE_TO_PROCEDURE_RUN_TYPE: Record<StudyType, string | null> = {
    [StudyType.ASSAY_DEVELOPMENT]: 'assay run',
    [StudyType.PROCESS_DEVELOPMENT]: 'recipe run',
    [StudyType.CLINICAL]: 'assay run',
    [StudyType.PRECLINICAL]: 'assay run',
    [StudyType.CUSTOM]: null,
  };
  let procedureRunUIString = STUDY_TYPE_TO_PROCEDURE_RUN_TYPE[studyType];
  assert(
    procedureRunUIString != null,
    `${studyType} studies do not have a respective procedure run UI string.`
  );
  if (plural) {
    procedureRunUIString = pluralize(2, procedureRunUIString);
  }
  if (capital) {
    procedureRunUIString = capitalize(procedureRunUIString);
  }
  if (includeArticle) {
    procedureRunUIString = prependAOrAn(procedureRunUIString);
  }
  return procedureRunUIString;
}

export function getProcedureRunConditionTextForProcedureType(
  procedureType: ProcedureType,
  {plural = false, capital = false}: {plural?: boolean; capital?: boolean} = {}
): string {
  const PROCEDURE_TYPE_TO_RUN_CONDITION_TEXT: Record<ProcedureType, string> = {
    [ProcedureType.ASSAY]: 'condition',
    [ProcedureType.RECIPE]: 'condition',
  };
  let label = PROCEDURE_TYPE_TO_RUN_CONDITION_TEXT[procedureType];
  if (plural) {
    label = pluralize(2, label);
  }
  if (capital) {
    label = capitalize(label);
  }
  return label;
}

const STUDY_TYPE_TEXT: Record<StudyType, string> = {
  [StudyType.CUSTOM]: 'Custom',
  [StudyType.PROCESS_DEVELOPMENT]: 'Process Development',
  [StudyType.ASSAY_DEVELOPMENT]: 'Assay Development',
  [StudyType.CLINICAL]: 'Clinical',
  [StudyType.PRECLINICAL]: 'Preclinical',
};

export function getStudyTypeText(studyType: StudyType): string {
  return STUDY_TYPE_TEXT[studyType];
}

export function getEnabledStudyTypes(): Array<StudyType> {
  const studyTypes: Array<StudyType> = [StudyType.CUSTOM];
  if (
    isFeatureEnabled({feature: RecipesFeature.RECIPES_PLAN_EXPERIMENT}) &&
    isFeatureEnabled({feature: RecipesFeature.RECIPES_CREATE_STUDY})
  ) {
    studyTypes.push(StudyType.PROCESS_DEVELOPMENT);
  }
  if (
    BenchlingConfig.STUDIES_ENABLE_ASSAY_DEVELOPMENT &&
    BenchlingConfig.PROCEDURES_ENABLE_ASSAYS &&
    isFeatureEnabled({feature: AssaysFeature.ASSAYS_CREATE_STUDY})
  ) {
    studyTypes.push(StudyType.ASSAY_DEVELOPMENT);
  }
  if (BenchlingConfig.STUDIES_ENABLE_CLINICAL) {
    studyTypes.push(StudyType.CLINICAL);
  }
  if (BenchlingConfig.STUDIES_ENABLE_PRECLINICAL) {
    studyTypes.push(StudyType.PRECLINICAL);
  }
  return studyTypes;
}

export function supportsManualIdGeneration(studyType: StudyType): boolean {
  return [StudyType.CLINICAL, StudyType.PRECLINICAL].includes(studyType);
}

export function isClinicalOrPreclinicalStudy(studyType: StudyType): boolean {
  return studyType === StudyType.CLINICAL || studyType === StudyType.PRECLINICAL;
}

export function backfillStudyTypeOnCreates(
  creates: Array<{studyId: string; studyType?: StudyType; procedureRunPlanId?: string}>
): Array<CreateExperimentTaskRequestMetadataCreate> {
  const backfilledCreates: Array<CreateExperimentTaskRequestMetadataCreate> = creates.map((create) => ({
    studyId: create.studyId,
    studyType: create.studyType ?? StudyType.ASSAY_DEVELOPMENT,
    ...(create.procedureRunPlanId ? {procedureRunPlanId: create.procedureRunPlanId} : {}),
  }));
  return backfilledCreates;
}

/** See `TypedStudyAdapter['review']['analysis']['disallowAdhocAnalysis']` for more info */
export function disallowsAdhocAnalysis(studyType: StudyType): boolean {
  return getTypedStudyAdapter(studyType).review?.analysis?.disallowAdhocAnalysis ?? false;
}
